import { jsonToCSV } from "../../../utils/easyMaps/csvAndJsonConverter";
import type { TableRow } from "./types";

// Colunas canônicas do CSV (ordem usada na exportação e no modelo).
export const CSV_HEADERS = [
  "tipo",
  "elemento",
  "titulo",
  "icone",
  "lat",
  "long",
  "tags",
  "descricao",
] as const;

// Separador das tags dentro de uma única célula do CSV (mantém o valor como um
// campo só, sem conflitar com a vírgula que separa colunas).
export const CSV_TAGS_SEPARATOR = ";";

export type CsvHeaderKey = (typeof CSV_HEADERS)[number];

// Um registro do CSV: chave canônica → célula (string já com trim).
export type CsvRecord = Partial<Record<CsvHeaderKey, string>>;

export interface CsvParseResult {
  records: CsvRecord[];
  /** true quando nenhuma coluna essencial do cabeçalho foi reconhecida. */
  headerInvalid: boolean;
  /** true quando o arquivo não possui linhas de dados. */
  empty: boolean;
}

// Aliases de cabeçalho (case-insensitive) para tolerar formatos legados
// (ex.: export do useMapMenu usa `hostname`/`img`) e nomes em inglês.
const HEADER_ALIASES: Record<string, CsvHeaderKey> = {
  tipo: "tipo",
  type: "tipo",
  elemento: "elemento",
  element: "elemento",
  host: "elemento",
  hostname: "elemento",
  titulo: "titulo",
  título: "titulo",
  title: "titulo",
  icone: "icone",
  ícone: "icone",
  icon: "icone",
  img: "icone",
  lat: "lat",
  latitude: "lat",
  long: "long",
  longitude: "long",
  tags: "tags",
  tag: "tags",
  etiquetas: "tags",
  etiqueta: "tags",
  descricao: "descricao",
  descrição: "descricao",
  description: "descricao",
};

// Colunas mínimas para considerar o cabeçalho válido.
const CORE_KEYS: CsvHeaderKey[] = ["tipo", "elemento", "lat", "long"];

// Tokeniza o CSV em linhas/células respeitando aspas (RFC-4180): aspas duplas
// escapam ("" → "), vírgulas e quebras de linha dentro de aspas são literais.
function tokenizeCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  while (i < n) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += char;
      i++;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (char === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (char === "\r") {
      if (text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      field = "";
      row = [];
      i++;
      continue;
    }
    if (char === "\n") {
      row.push(field);
      rows.push(row);
      field = "";
      row = [];
      i++;
      continue;
    }

    field += char;
    i++;
  }

  row.push(field);
  rows.push(row);
  return rows;
}

function isEmptyRow(cells: string[]): boolean {
  return cells.every((c) => c.trim() === "");
}

export function parseCsv(text: string): CsvParseResult {
  // Remove BOM inicial (arquivos exportados pelo Excel) antes de qualquer parse.
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  if (clean.trim() === "") {
    return { records: [], headerInvalid: false, empty: true };
  }

  const rows = tokenizeCsv(clean).filter((r) => !isEmptyRow(r));
  if (rows.length === 0) {
    return { records: [], headerInvalid: false, empty: true };
  }

  const colKeys: (CsvHeaderKey | null)[] = rows[0].map(
    (h) => HEADER_ALIASES[h.trim().toLowerCase()] ?? null,
  );
  const recognized = new Set(colKeys.filter(Boolean) as CsvHeaderKey[]);
  const headerInvalid = !CORE_KEYS.some((k) => recognized.has(k));
  if (headerInvalid) {
    return { records: [], headerInvalid: true, empty: false };
  }

  const dataRows = rows.slice(1);
  if (dataRows.length === 0) {
    return { records: [], headerInvalid: false, empty: true };
  }

  const records: CsvRecord[] = dataRows.map((cells) => {
    const rec: CsvRecord = {};
    colKeys.forEach((key, idx) => {
      if (!key) return;
      const value = cells[idx];
      if (value !== undefined) rec[key] = value.trim();
    });
    return rec;
  });

  return { records, headerInvalid: false, empty: false };
}

export function parseCsvFile(file: File): Promise<CsvParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(parseCsv(String(e.target?.result ?? "")));
    reader.onerror = () =>
      reject(reader.error ?? new Error("Falha ao ler o arquivo"));
    reader.readAsText(file);
  });
}

// Serializa objetos (com chaves de CSV_HEADERS) em CSV. Garante a linha de
// cabeçalho mesmo quando não há linhas e delega o escaping ao jsonToCSV.
export function rowsToCsv(
  objects: Array<Record<CsvHeaderKey, string>>,
): string {
  if (!objects.length) return CSV_HEADERS.join(",");
  const normalized = objects.map((obj) => {
    const row: Record<string, string> = {};
    CSV_HEADERS.forEach((key) => {
      row[key] = obj[key] ?? "";
    });
    return row;
  });
  return jsonToCSV(normalized);
}

// Modelo de exemplo: cabeçalho + linhas de amostra. A coluna `icone` é deixada
// em branco de propósito para demonstrar o preenchimento do ícone padrão.
export function exampleCsvTemplate(): string {
  return [
    CSV_HEADERS.join(","),
    "Host,meu-host-01,Roteador Sede,,-23.5505,-46.6333,core;backbone,Exemplo de host",
    "POP,POP Centro,,,-23.5510,-46.6340,acesso,Exemplo de POP",
  ].join("\n");
}

export interface RowMapDeps {
  icons: any[];
  validIconNames: Set<string>;
  popsByTitulo: Map<string, any>;
  mapsByTitulo: Map<string, any>;
  normalizeType: (v: any) => "Host" | "POP" | "Mapa";
  defaultIconNameForType: (
    type: "Host" | "POP" | "Mapa",
    icons: any[],
  ) => string | null;
  newId: () => string;
}

export interface RowMapOutcome {
  row: TableRow;
  /** tipo informado era não-vazio e desconhecido → ajustado para Host. */
  tipoDefaulted: boolean;
  /** ícone informado era não-vazio e inválido → ajustado para o padrão. */
  iconDefaulted: boolean;
}

// Converte um registro do CSV em uma TableRow completa, espelhando a lógica de
// handleAfterChange/fillRowDefaults: normaliza o tipo, valida o ícone contra a
// lista conhecida (caindo no padrão quando inválido) e resolve o id de POP/Mapa.
export function mapRecordToRow(
  rec: CsvRecord,
  deps: RowMapDeps,
): RowMapOutcome {
  const rawTipo = (rec.tipo ?? "").trim();
  const type = deps.normalizeType(rawTipo);
  const tipoDefaulted =
    rawTipo !== "" && type === "Host" && rawTipo.toLowerCase() !== "host";

  const selectedName = (rec.elemento ?? "").trim() || null;

  const rawIcon = (rec.icone ?? "").trim();
  let iconId: string | null;
  let iconDefaulted = false;
  if (rawIcon && deps.validIconNames.has(rawIcon)) {
    iconId = rawIcon;
  } else {
    iconId = deps.defaultIconNameForType(type, deps.icons);
    iconDefaulted = rawIcon !== "";
  }

  const lat = (rec.lat ?? "").trim() || null;
  const long = (rec.long ?? "").trim() || null;
  const description = (rec.descricao ?? "").trim() || null;

  // Tags: separadas por ";" na célula, com trim, sem vazias e sem duplicatas
  // (case-insensitive, preservando a primeira ocorrência).
  const tags: string[] = [];
  const seenTags = new Set<string>();
  (rec.tags ?? "")
    .split(CSV_TAGS_SEPARATOR)
    .map((t) => t.trim())
    .filter((t) => t !== "")
    .forEach((t) => {
      const key = t.toLowerCase();
      if (!seenTags.has(key)) {
        seenTags.add(key);
        tags.push(t);
      }
    });

  const row: TableRow = {
    id: deps.newId(),
    type,
    selectedName,
    title: null,
    iconId,
    lat,
    long,
    hostItems: [],
    description,
    tags,
    _errors: {},
    checked: false,
  };

  if (type === "Host") {
    row.title = selectedName;
  } else if (type === "POP") {
    const sel = selectedName ? deps.popsByTitulo.get(selectedName) : undefined;
    row.mapRefId = sel?.id ?? null;
  } else if (type === "Mapa") {
    const sel = selectedName ? deps.mapsByTitulo.get(selectedName) : undefined;
    row.mapRefId = sel?.id ?? null;
  }

  return { row, tipoDefaulted, iconDefaulted };
}
